// Which visit a chart / a recording is on, and whether it is TODAY's visit.
// Pure functions (no React, no network), unit tested under node:
//   node --no-warnings src/utils/visitGuard.test.mjs
//
// Owner rules (2026-09-29, patient 8823):
//   1. When the patient has an appointment today, a chart NEVER opens on a past
//      appointment. Every opener resolves through one resolver
//      (chartVisitResolver.js) that picks today's appointment; a past visit
//      opens only when there is no visit today, and it is labelled
//      "Past visit · 09-08 · Dr. Adupa".
//   2. A recording never starts on an appointment other than today's without
//      two explicit confirmations (VisitConsentHost).
//
// "Today" is the clinic's calendar day (America/Chicago), whatever time zone
// the browser is in.
//
// Rows are dashboard cards (POST /appointments/dashboard) or
// GET /appointments/{patientid}/future rows. Both carry appointmentid,
// patientid, startdate ("YYYY-MM-DD HH:MM:SS"), starttime ("08:40:00"),
// visit_confirmation; dashboard cards also carry start_time ("08:40 AM"),
// linked_provider and providername.

import {
  clinicTodayKey,
  startDateKey,
  APPT_ID_PLACEHOLDER,
} from "../landingscreen/cards/visitflow.js";

// Visits that no longer happen. Rescheduled = moved to another day.
const CANCELLED_STATUSES = new Set(["cancelled", "canceled", "rescheduled"]);

export const isCancelledVisit = (row) => {
  const vc = String(row?.visit_confirmation ?? "").trim().toLowerCase();
  if (CANCELLED_STATUSES.has(vc)) return true;
  // Athena's appointmentstatus code for a cancelled slot.
  const st = String(row?.appointmentstatus ?? "").trim().toLowerCase();
  return st === "x";
};

// A no-show (visit_confirmation "No Show" / "no show"; owner 10-09). NOT part of
// isCancelledVisit: the chart resolver and the recording gate still treat a
// no-show today as today's visit (the patient may arrive late, 09-29 rule).
const NO_SHOW_STATUSES = new Set(["no show", "noshow", "no-show", "no_show"]);

export const isNoShowVisit = (row) =>
  NO_SHOW_STATUSES.has(String(row?.visit_confirmation ?? "").trim().toLowerCase().replace(/\s+/g, " "));

// A visit that did not (or will not) happen: cancelled, rescheduled or no-show.
// Its date is never shown as a chart's date of service (CareIQ DOS row, 10-09).
export const visitNotHeld = (row) => isCancelledVisit(row) || isNoShowVisit(row);

// "Cancelled" | "Rescheduled" | "No show" | "" — the label a visit that did not
// happen carries wherever its date is shown.
export const visitStatusWord = (row) => {
  if (isNoShowVisit(row)) return "No show";
  const vc = String(row?.visit_confirmation ?? "").trim().toLowerCase();
  if (vc === "rescheduled") return "Rescheduled";
  return isCancelledVisit(row) ? "Cancelled" : "";
};

// A real appointment id (junk rows carry INT max; patient-only cards none).
export const hasAppointmentId = (v) => {
  if (v === null || v === undefined || v === "") return false;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 && n < APPT_ID_PLACEHOLDER;
};

export const samePatient = (a, b) => {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  return x !== "" && x === y;
};

export const sameAppointmentId = (a, b) =>
  hasAppointmentId(a) && hasAppointmentId(b) && Number(a) === Number(b);

// "YYYY-MM-DD" clinic day of the row's appointment, or null.
export const visitDayKey = (row) => startDateKey(row?.startdate) || null;

// "2026-09-08" -> "09-08" (the owner's wording: "Past visit · 09-08").
export const shortDate = (dayKey) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ""));
  return m ? `${m[2]}-${m[3]}` : "";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-09-11" -> "Sep 11, 2026" (the CareIQ tab's and the consent's wording); "" when not a date.
export const longDate = (dayKey) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dayKey || ""));
  if (!m) return "";
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return "";
  return `${MONTHS[mo - 1]} ${Number(m[3])}, ${m[1]}`;
};

// "Reshmi Adupa, MD" -> "Dr. Adupa"; "Dr. Madhu" stays; other names and
// credentials (NP, PA) stay as written - only MD/DO become "Dr.".
export const shortProviderName = (name) => {
  const s = String(name ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  if (/^dr\.?\s/i.test(s)) return s.replace(/^dr\.?\s+/i, "Dr. ");
  const m = /^(.*?),\s*(md|do)\b\.?/i.exec(s);
  if (m) {
    const parts = m[1].trim().split(" ").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last) return `Dr. ${last}`;
  }
  return s;
};

export const visitProvider = (row) =>
  shortProviderName(
    String(row?.linked_provider ?? "").trim() ||
      String(row?.providername ?? "").trim() ||
      String(row?.provider ?? "").trim(),
  );

// Minutes after midnight of the scheduled start, or null.
export const visitStartMinutes = (row) => {
  const fromHm = (h, m, ampm) => {
    let hh = Number(h);
    const mm = Number(m);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (ampm) {
      const pm = /p/i.test(ampm);
      hh = (hh % 12) + (pm ? 12 : 0);
    }
    if (hh > 23 || mm > 59) return null;
    return hh * 60 + mm;
  };
  const st = String(row?.start_time ?? "").trim();
  let m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/.exec(st);
  if (m) return fromHm(m[1], m[2], m[3]);
  const t = String(row?.starttime ?? "").trim();
  m = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*([AaPp][Mm])?$/.exec(t);
  if (m) return fromHm(m[1], m[2], m[3]);
  const sd = String(row?.startdate ?? "").trim();
  m = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/.exec(sd);
  if (m && !(m[1] === "00" && m[2] === "00")) return fromHm(m[1], m[2]);
  return null;
};

// "8:40 AM", or "" when the row has no start time.
export const visitTimeLabel = (row) => {
  const mins = visitStartMinutes(row);
  if (mins === null) return "";
  const h = Math.floor(mins / 60);
  const mm = String(mins % 60).padStart(2, "0");
  return `${h % 12 || 12}:${mm} ${h >= 12 ? "PM" : "AM"}`;
};

// kind: "today" | "past" | "future" | "unknown" (appointment, no date) |
//       "none" (no appointment at all)
export const classifyVisit = (row, now = new Date()) => {
  const todayKey = clinicTodayKey(now);
  const dayKey = visitDayKey(row);
  const hasAppt = hasAppointmentId(row?.appointmentid);
  let kind;
  if (!hasAppt) kind = "none";
  else if (!dayKey) kind = "unknown";
  else if (dayKey === todayKey) kind = "today";
  else kind = dayKey < todayKey ? "past" : "future";
  return {
    kind,
    appointmentid: hasAppt ? String(Number(row.appointmentid)) : "",
    patientid: row?.patientid ?? null,
    dayKey,
    todayKey,
    dateShort: shortDate(dayKey),
    time: visitTimeLabel(row),
    provider: visitProvider(row),
    cancelled: isCancelledVisit(row),
    // "Cancelled" | "Rescheduled" | "No show" | "" (visitStatusWord)
    statusWord: visitStatusWord(row),
  };
};

// The chart card is a visit happening TODAY: today's clinic date and neither
// cancelled, rescheduled nor no-show. Only such a card gives CareIQ a date of
// service and a visit to work on; any other card opens CareIQ on "No visit
// today" (owner 10-09).
export const isLiveTodayVisit = (row, now = new Date()) =>
  classifyVisit(row, now).kind === "today" && !visitNotHeld(row);

export const isTodayVisit = (info) => info?.kind === "today";

const joinDot = (...parts) => parts.filter(Boolean).join(" · ");

// " (cancelled)" / " (no show)" / " (rescheduled)" after a past or future
// visit that did not happen (10-09: the header named such a visit plainly).
const statusSuffix = (info) => (info && info.statusWord ? ` (${info.statusWord.toLowerCase()})` : "");

// Chart header / banner label.
export const visitLabel = (info) => {
  if (!info) return "";
  switch (info.kind) {
    case "today":
      return joinDot("Today's visit", info.time, info.provider) + (info.cancelled ? " (cancelled)" : "");
    case "past":
      return joinDot("Past visit", info.dateShort, info.provider) + statusSuffix(info);
    case "future":
      return joinDot("Future visit", info.dateShort, info.provider) + statusSuffix(info);
    case "unknown":
      return joinDot(`Visit #${info.appointmentid}`, "date unknown", info.provider);
    default:
      return "No visit";
  }
};

// The recording panel's "where is this note going" line.
export const recordingTargetText = (info) => {
  if (!info) return "";
  switch (info.kind) {
    case "today":
      return joinDot("Recording for today's visit", info.time, info.provider) + statusSuffix(info);
    case "past":
    case "future":
      return `${joinDot(`Recording for the ${info.dateShort} visit`, info.provider)} (not today${
        info.statusWord ? `, ${info.statusWord.toLowerCase()}` : ""
      })`;
    case "unknown":
      return `${joinDot(`Recording for visit #${info.appointmentid}`, info.provider)} (date unknown)`;
    default:
      return "Recording is not attached to a visit";
  }
};

// "the 09-08 visit with Dr. Adupa"
export const visitPhrase = (info) => {
  if (!info) return "";
  const withProv = info.provider ? ` with ${info.provider}` : "";
  if (info.kind === "past" || info.kind === "future") return `the ${info.dateShort} visit${withProv}`;
  if (info.kind === "unknown") return `visit #${info.appointmentid}${withProv}`;
  if (info.kind === "today") return `today's visit${withProv}`;
  return "";
};

// Every recording start on anything but today's visit needs the two-step
// consent. Today's visit never waits on anything -- except a chart recording on
// a SELECTED visit (owner 10-09: the mic records on the visit the provider
// selects): today's visit that did not happen (cancelled / rescheduled /
// no-show) is then confirmed too, with a warning ({ confirmNotHeld: true }).
export const needsRecordingConsent = (info, { confirmNotHeld = false } = {}) =>
  !info || info.kind !== "today" || (confirmNotHeld && Boolean(info.statusWord));

// The warning a visit that did not happen carries in the consent (10-09).
const notHeldWarning = (info) =>
  info && info.statusWord
    ? `This visit is marked ${info.statusWord.toLowerCase()}. Record on it only if the patient was really seen for this visit.`
    : "";

// Text of the two consent steps. subject "chart": a new recording from the
// patient chart, on the visit the provider selected ("Record on the Sep 11,
// 2026 visit?"); "note": audio added to (or voice edits of) an existing note
// in the note editor. warning: "" or the not-held visit's warning.
export const consentCopy = (info, subject = "chart") => {
  const phrase = visitPhrase(info);
  if (!info || info.kind === "none" || !phrase) {
    return {
      title: "No visit on this chart",
      step1:
        "This chart is not open on a visit today. The recording will not be filed to any visit.",
      warning: "",
      tickLabel: "I confirm this recording is not for a visit today",
      typeTarget: "",
    };
  }
  const warning = notHeldWarning(info);
  if (subject === "note") {
    return {
      title: info.kind === "future" ? "Not today's visit (future)" : "Not today's visit",
      step1: `This note is filed to ${phrase}, not a visit today. What you record will change that visit's note, which is signed into its Athena encounter.`,
      warning,
      tickLabel: `I confirm this recording belongs to ${phrase}`,
      typeTarget: info.dateShort || "",
    };
  }
  const day = longDate(info.dayKey);
  const withProv = info.provider ? ` with ${info.provider}` : "";
  const named =
    (info.kind === "past" || info.kind === "future") && day
      ? `the ${day} visit${withProv}`
      : info.kind === "today" && info.time
        ? `today's ${info.time} visit${withProv}`
        : phrase;
  const title =
    info.kind === "today"
      ? "Record on today's visit?"
      : day
        ? `Record on the ${day} visit?`
        : `Record on ${named}?`;
  const where = info.kind === "today" ? named : `${named}, not a visit today`;
  return {
    title,
    step1: `You selected ${where}. The recording will be filed to that visit and signed into its Athena encounter.`,
    warning,
    tickLabel: `I confirm this recording belongs to ${named}`,
    typeTarget: info.dateShort || "",
  };
};

// "9/8", "09-08", "09.08", "0908" -> "09-08"; anything else -> "".
export const normalizeTypedDate = (typed) => {
  const s = String(typed ?? "").trim();
  let m = /^(\d{1,2})\s*[-/.]\s*(\d{1,2})$/.exec(s);
  if (!m) m = /^(\d{2})(\d{2})$/.exec(s);
  if (!m) return "";
  const mo = Number(m[1]);
  const d = Number(m[2]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  return `${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// Step 2 passes with the box ticked, or the visit date typed exactly.
export const consentStep2Valid = ({ typed, ticked } = {}, info) => {
  if (ticked === true) return true;
  const target = consentCopy(info).typeTarget;
  return Boolean(target) && normalizeTypedDate(typed) === target;
};

// Resource schedules, not a provider visit: labs, ultrasound, echo, ECG,
// injections... In practice 9928 these are the "providers" 5 (Art: echo /
// doppler), 6 (Procedures: labs, injections, ECG) and 14 (ULTRASOUND); the
// purpose / provider-name patterns cover the same slots when the id is absent.
const RESOURCE_PROVIDER_IDS = new Set(["5", "6", "14"]);
const RESOURCE_PROVIDER_NAME_RE = /^\s*(ultrasound|procedures)\b/i;
const RESOURCE_PURPOSE_RE =
  /\b(labs?|lab orders|ultrasound|us|echo|echocardiogram|ecg|ekg|doppler|carotid|injections?|vaccines?|shingles|biat|estim|retinoscopy|sleep study|reevue)\b/i;

export const isResourceSlot = (row) => {
  if (!row) return false;
  const pid = String(row.providerid ?? "").trim();
  if (pid && RESOURCE_PROVIDER_IDS.has(pid)) return true;
  const names = [row.providername, row.linked_provider, row.provider].map((v) => String(v ?? ""));
  if (names.some((n) => RESOURCE_PROVIDER_NAME_RE.test(n))) return true;
  const purpose = String(row.purpose ?? row.type ?? "");
  return RESOURCE_PURPOSE_RE.test(purpose);
};

// The patient's appointment TODAY that a chart must open on: among today's
// non-cancelled rows, the provider visit before any resource slot (a LAB /
// ULTRASOUND / ECHO row can be booked earlier than the provider visit), then
// the earliest scheduled start, ties by appointment id. null when there is none.
export const pickTodayRow = (rows, patientid, now = new Date()) => {
  const todayKey = clinicTodayKey(now);
  const list = (Array.isArray(rows) ? rows : []).filter(
    (r) =>
      r &&
      (patientid === undefined || patientid === null || samePatient(r.patientid, patientid)) &&
      hasAppointmentId(r.appointmentid) &&
      visitDayKey(r) === todayKey &&
      !isCancelledVisit(r),
  );
  if (!list.length) return null;
  const key = (r) => {
    const m = visitStartMinutes(r);
    return m === null ? 24 * 60 : m;
  };
  const resource = (r) => (isResourceSlot(r) ? 1 : 0);
  return [...list].sort(
    (a, b) =>
      resource(a) - resource(b) ||
      key(a) - key(b) ||
      Number(a.appointmentid) - Number(b.appointmentid),
  )[0];
};

// The loaded dashboard row a P-card refresh should show for the chart's card:
// same row id AND the same appointment (or, for a card without one, the same
// patient). appointment_master and appointment_master_history ids overlap, so
// an id alone can name another patient's card. null when none matches.
export const findPcardRow = (rows, id, ref) => {
  const list = Array.isArray(rows) ? rows : [];
  return (
    list.find((y) => {
      if (!y || y.id === undefined || y.id === null || String(y.id) !== String(id)) return false;
      if (!ref) return false;
      if (hasAppointmentId(ref.appointmentid) || hasAppointmentId(y.appointmentid)) {
        return sameAppointmentId(y.appointmentid, ref.appointmentid) && (
          ref.patientid === undefined || ref.patientid === null || ref.patientid === "" ||
          samePatient(y.patientid, ref.patientid));
      }
      return samePatient(y.patientid, ref.patientid);
    }) || null
  );
};

// A chart opened on this card must first look for today's visit.
export const needsChartResolution = (card, now = new Date()) => {
  const info = classifyVisit(card, now);
  return !(info.kind === "today" && !info.cancelled);
};

// Appointment fields a /future row supplies. That endpoint returns
// linked_provider AS "providername" and purpose AS "type".
const MERGE_FIELDS = [
  "id",
  "appointmentid",
  "departmentid",
  "providerid",
  "startdate",
  "starttime",
  "appointmenttypeid",
  "visit_confirmation",
  "status",
  "token",
  "checkin",
  "checkout",
  "signintime",
];

// Per-appointment card fields that belonged to the OTHER appointment.
const RESET_FIELDS = {
  encounterid: null,
  lov: null,
  tov: null,
  lovtov: null,
  room_no: "",
  orders: [],
  allappts: [],
};

// A dashboard card for today's appointment built from a card of the same
// patient (patient fields) and a /future row (the appointment). Used only when
// the dashboard did not return today's row itself (it shows one card per
// patient and can pick a cancelled row first, or the user's location view
// hides it).
export const mergeTodayAppointment = (base, appt) => {
  const out = { ...(base || {}), ...RESET_FIELDS };
  delete out.history;
  for (const f of MERGE_FIELDS) {
    if (appt && appt[f] !== undefined) out[f] = appt[f];
  }
  const linked = String(appt?.providername ?? appt?.linked_provider ?? "").trim();
  out.linked_provider = linked || null;
  out.providername = linked || null;
  out.purpose = appt?.type ?? appt?.purpose ?? out.purpose ?? null;
  const t = visitTimeLabel(appt);
  out.start_time = t || null;
  out.patientid = appt?.patientid ?? out.patientid;
  return out;
};

// Normalise an API body (axios interceptor returns the body) to a row list.
export const rowsOfBody = (body) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return [];
};

// The chart-open toast after the resolver switched visits.
export const switchedVisitMessage = (from, to) => {
  const target = joinDot(to?.time, to?.provider);
  const head = `Opened today's visit${target ? ` (${target})` : ""}.`;
  if (from && (from.kind === "past" || from.kind === "future") && from.dateShort) {
    return `${head} The ${from.dateShort} visit is under Prior Visits.`;
  }
  return head;
};

// BlinkScribe refused the recording because it belongs to another practice
// than the one this session is signed in to (practice scope guard): WebSocket
// close 4403 / reason or status code 'practice_scope'. Also 4400 /
// 'invalid_file_name' (the recording's file name was refused). Reconnecting
// or falling back to a local recording cannot succeed: its upload is refused
// the same way.
export const PRACTICE_SCOPE_MESSAGE =
  "This recording belongs to another practice than the one you are signed in to. " +
  "Sign in to that practice to record for this patient.";

export const isPracticeScopeRefusal = ({ code, reason } = {}) => {
  if (Number(code) === 4403) return true;
  return /practice_scope/i.test(String(reason ?? ""));
};

const isFileNameRefusal = ({ code, reason } = {}) =>
  Number(code) === 4400 || /invalid_file_name/i.test(String(reason ?? ""));

// The message to show for a refusal: the practice-scope sentence first (the
// server detail, when there is one, names the practices), else the server
// message / close reason, else `fallback`.
export const refusalMessage = ({ code, reason, message } = {}, fallback = "") => {
  const detail = String(message ?? "").trim();
  if (isPracticeScopeRefusal({ code, reason })) {
    return detail ? `${PRACTICE_SCOPE_MESSAGE} (${detail})` : PRACTICE_SCOPE_MESSAGE;
  }
  if (detail) return detail;
  if (isFileNameRefusal({ code, reason })) return "BlinkScribe refused this recording's file name.";
  return String(reason ?? "").trim() || fallback;
};

// A server refusal of a recording that must stop the session (no reconnect, no
// local fallback): the visit guard — WebSocket close 4409
// (PAST_VISIT_WHILE_TODAY_EXISTS) or 4428 (PAST_VISIT_CONFIRMATION_REQUIRED),
// or an error code / reason naming it — and the practice scope guard (4403 /
// 'practice_scope', 4400 / 'invalid_file_name').
export const isVisitRefusal = ({ code, reason } = {}) => {
  if (Number(code) === 4409 || Number(code) === 4428) return true;
  if (isPracticeScopeRefusal({ code, reason }) || isFileNameRefusal({ code, reason })) return true;
  const text = String(reason ?? "");
  return /PAST_VISIT|VISIT_(NOT_)?CONFIRM|STALE_VISIT/i.test(text);
};
